/**
 * Pure model behind the dense layer tree (components/map/LayerTree.js).
 *
 * Every drawable thing in the map workspace becomes one "entry" with a stable key:
 *   proj:<layerId>  project (demo/USGS) layer      imp:<id>  imported file layer
 *   pds:<datasetId> a project dataset not loaded yet (lazy row; becomes imp:/res: once loaded)
 *   res:<id>        analysis / dataset / borehole  ras:<id>  raster dataset
 *   vt:<id>         vector-tile layer              svc:<id>  external service layer
 *   survey          the aggregated survey block (lines, soundings, anomalies, annotations);
 *                   always present, never shown as a row, but part of the draw order.
 * `layerOrder` (hooks/useMapLayers) is an array of entry keys, BOTTOM to TOP of the draw
 * stack. The tree lists the top of the stack first (QGIS style). No React, no DOM.
 */
import { METHOD_COLORS } from './gis-types';
import { sampleRamp } from './classify';

export const SURVEY_KEY = 'survey';

export const GROUPS = [
  { id: 'project', label: 'Project data' },
  { id: 'project-datasets', label: 'Project datasets' },
  { id: 'imported', label: 'Imported' },
  { id: 'results', label: 'Results' },
  { id: 'viewing', label: 'Viewing (not saved)' },
  { id: 'rasters', label: 'Rasters' },
  { id: 'vector-tiles', label: 'Vector tiles' },
  { id: 'boreholes', label: 'Boreholes' },
  { id: 'services', label: 'Services' },
];
export const GROUP_LABEL = Object.fromEntries(GROUPS.map((g) => [g.id, g.label]));

const SURVEY_SOURCES = ['survey-lines-src', 'electrodes-src', 'line-labels-src', 'soundings-src', 'anomalies-src', 'ai-line-src', 'annotations-src'];

/** Default draw position (lower = drawn first, i.e. further down). Mirrors the old fixed JSX order. */
const RANK = { ras: 0, survey: 1, res: 2, svc: 3, proj: 4, pds: 4, imp: 5, vt: 6 };
export function keyPrefix(key) { const i = String(key).indexOf(':'); return i < 0 ? String(key) : key.slice(0, i); }
export function defaultRank(key) { return RANK[keyPrefix(key)] ?? 5; }

/** Entries whose ids survive a reload (the rest are session-only ids and are not persisted). */
export function isStableKey(key) { return ['proj', 'ras', 'vt', 'survey'].includes(keyPrefix(key)); }

// ── geometry helpers ────────────────────────────────────────────────────────

/** [[minLon,minLat],[maxLon,maxLat]] of a FeatureCollection, or null. Scans at most `cap` features. */
export function geojsonBounds(geojson, cap = 20000) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity, found = 0;
  const scan = (c) => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === 'number') {
      if (!Number.isFinite(c[0]) || !Number.isFinite(c[1])) return;
      if (c[0] < minX) minX = c[0];
      if (c[0] > maxX) maxX = c[0];
      if (c[1] < minY) minY = c[1];
      if (c[1] > maxY) maxY = c[1];
      found++;
    } else c.forEach(scan);
  };
  (geojson?.features || []).slice(0, cap).forEach((f) => f?.geometry?.coordinates && scan(f.geometry.coordinates));
  return found ? [[minX, minY], [maxX, maxY]] : null;
}

/** 'point' | 'line' | 'fill' from the first feature (used to pick the swatch shape). */
export function dominantGeometry(geojson) {
  const t = geojson?.features?.[0]?.geometry?.type || '';
  if (t.includes('Polygon')) return 'fill';
  if (t.includes('Line')) return 'line';
  return 'point';
}

// ── swatches ────────────────────────────────────────────────────────────────

const HEAT = ['#2563eb', '#00b8de', '#34d399', '#fbbf24', '#f87171'];

/** Swatch reflecting the layer's current style: solid colour or a ramp for classified styles. */
export function swatchFor(style, fallback) {
  if (style) {
    if (style.mode && style.mode !== 'single' && style.colorExpr) {
      return { ...fallback, type: 'ramp', colors: sampleRamp(style.ramp, 5) };
    }
    return { ...fallback, type: 'solid', color: style.fillColor || style.strokeColor || fallback.color, stroke: style.strokeColor };
  }
  return fallback;
}

// ── entries ─────────────────────────────────────────────────────────────────

/**
 * What a result layer was derived from: a dataset (`layer.dataset`) or a stored inversion model
 * (`layer.source = { kind: 'model', id, name }`). Layers that share a source nest under one
 * sub-group row in the tree; layers with neither are view-only (not stored anywhere).
 */
export function resultSource(r) {
  if (r?.dataset?.id) return { key: `ds:${r.dataset.id}`, kind: 'dataset', name: r.dataset.name || '' };
  if (r?.source?.kind === 'model' && r.source.id) return { key: `model:${r.source.id}`, kind: 'model', name: r.source.name || '' };
  return null;
}

/** Row label inside a sub-group: drops the " — <source>" suffix ("Pseudosection — AGI56DD" -> "Pseudosection"). */
export function shortLayerName(name, sourceName) {
  const suffix = sourceName ? ` — ${sourceName}` : '';
  return suffix && name.endsWith(suffix) && name.length > suffix.length ? name.slice(0, -suffix.length) : name;
}

const featureCount = (gj) => (gj?.features ? gj.features.length : null);

/**
 * All entries for the current workspace. Inputs are the hook outputs MapView already has;
 * everything is optional so the tree renders with whatever is loaded.
 */
export function buildEntries({
  layers = [], usgsData = {}, importedLayers = [], resultLayers = [], rasterLayers = [],
  vtLayers = [], serviceLayers = [], layerStyles = {}, layerOpacity = {},
  projectDatasets = [], projectDatasetStatus = {},
} = {}) {
  const pdsIds = new Set(projectDatasets.map((d) => d.id));
  const entries = [];
  entries.push({ key: SURVEY_KEY, hidden: true, group: 'project', kind: 'survey', name: 'Survey base', visible: true, sources: SURVEY_SOURCES });

  layers.forEach((l) => {
    const gj = usgsData[l.id];
    const hasData = !!gj?.features?.length;
    entries.push({
      key: `proj:${l.id}`, id: l.id, group: 'project', kind: 'project', name: l.name, visible: !!l.visible,
      opacity: l.opacity ?? 100, count: featureCount(gj),
      campaign: l.campaign || null, method: l.method, date: l.date, anomalies: l.anomalies || 0,
      styleId: hasData ? `usgs-${l.id}` : null, geojson: gj || null, sources: [`usgs-${l.id}`],
      zoomable: hasData, exportable: hasData, hasTable: hasData,
      swatch: swatchFor(layerStyles[`usgs-${l.id}`], { type: 'solid', color: METHOD_COLORS[l.method] || '#8b949e', shape: dominantGeometry(gj) }),
    });
  });

  importedLayers.forEach((l) => {
    entries.push({
      key: `imp:${l.id}`, id: l.id, group: l.projectDatasetId ? 'project-datasets' : 'imported', kind: 'imported', name: l.name, visible: !!l.visible,
      opacity: layerOpacity[l.id] ?? 100, count: featureCount(l.geojson), styleId: l.id, geojson: l.geojson,
      sources: [l.id], removable: true, renamable: true, duplicable: true, exportable: true, zoomable: true, hasTable: true,
      datasetId: l.projectDatasetId || null, datasetKind: l.projectDatasetId ? 'file' : null, datasetName: l.projectDatasetId ? l.name : null,
      swatch: swatchFor(layerStyles[l.id], l.xyz
        ? { type: 'ramp', colors: HEAT, shape: 'point' }
        : { type: 'solid', color: '#a78bfa', shape: dominantGeometry(l.geojson) }),
    });
  });

  resultLayers.forEach((r) => {
    const isBorehole = r.kind === 'boreholes';
    const dsKind = r.dataset?.kind;
    let fallback = { type: 'solid', color: '#00d4ff', shape: dominantGeometry(r.geojson) };
    if (r.kind === 'grid') fallback = { type: 'ramp', colors: HEAT, shape: 'point' };
    else if (dsKind === 'electrodes') fallback = { type: 'solid', color: '#ffffff', shape: 'point' };
    else if (dsKind === 'pseudosection') fallback = { type: 'solid', color: '#fbbf24', shape: 'point' };
    else if (isBorehole) fallback = { type: 'solid', color: '#ffffff', stroke: '#0b1220', shape: 'point' };
    const src = resultSource(r);
    const group = isBorehole ? 'boreholes' : (r.dataset?.id && pdsIds.has(r.dataset.id) ? 'project-datasets' : (src || r.dataset || r.source ? 'results' : 'viewing'));
    entries.push({
      key: `res:${r.id}`, id: r.id, group, kind: 'result', name: r.name, visible: r.visible !== false,
      sourceKey: src && !isBorehole ? src.key : null, sourceKind: src?.kind || null, sourceName: src?.name || null,
      shortName: src && !isBorehole ? shortLayerName(r.name, src.name) : r.name,
      opacity: layerOpacity[r.id] ?? 100, count: featureCount(r.geojson), styleId: r.id, geojson: r.geojson, datasetUrl: r.dataset?.url || null,
      isDataset: !!r.dataset, datasetId: r.dataset?.id || null, datasetKind: r.dataset?.kind || null, datasetName: r.dataset?.name || null,
      sources: [`result-${r.id}`], removable: true, renamable: !r.dataset, duplicable: !r.dataset,
      exportable: true, zoomable: true, hasTable: true,
      swatch: swatchFor(layerStyles[r.id], fallback),
    });
  });

  // Project datasets not drawn yet: unchecked lazy rows. Ticking one loads it (hooks/useProjectDatasetLayers).
  projectDatasets.forEach((d) => {
    const loaded = importedLayers.some((l) => l.projectDatasetId === d.id) || resultLayers.some((r) => r.dataset?.id === d.id);
    if (loaded) return;
    const st = projectDatasetStatus[d.id];
    const note = { loading: 'loading…', error: 'failed to load', 'needs-georef': 'needs georeference', 'needs-mapping': 'map columns' }[st];
    entries.push({
      key: `pds:${d.id}`, id: d.id, group: 'project-datasets', kind: 'project-dataset', name: note ? `${d.name} (${note})` : d.name,
      visible: false, opacity: null, count: null, lazy: true, status: st || 'idle', sources: [],
      swatch: { type: 'solid', color: '#8b949e', shape: d.kind === 'raw' || d.kind === 'tabular' ? 'point' : 'line' },
    });
  });

  rasterLayers.forEach((r) => {
    entries.push({
      key: `ras:${r.id}`, id: r.id, group: 'rasters', kind: 'raster', name: r.name, visible: !!r.visible,
      opacity: Math.round((r.opacity ?? 1) * 100), count: null, removable: true, zoomable: !!r.bounds,
      styleId: r.id, // Properties… opens the panel's raster section (colormap, range, DEM product, contours)
      sources: [`${r.id}-src-${r.rev}`, `${r.id}-contours-src`],
      swatch: { type: 'ramp', colors: [], colormap: r.style?.colormap || 'viridis', shape: 'fill' },
    });
  });

  vtLayers.forEach((l) => {
    entries.push({
      key: `vt:${l.id}`, id: l.id, group: 'vector-tiles', kind: 'vector-tile', name: l.name, visible: !!l.visible,
      opacity: null, count: l.featureCount ? Number(l.featureCount) : null, zoomable: !!l.bounds, bounds: l.bounds || null,
      styleId: l.id, // Properties… opens the panel's vector-tile section
      sources: [l.sourceId], zFields: l.zFields || [], zField: l.zField || null,
      swatch: { type: 'solid', color: '#00d4ff', shape: 'point' },
    });
  });

  serviceLayers.forEach((s) => {
    entries.push({
      key: `svc:${s.id}`, id: s.id, group: 'services', kind: 'service', name: s.name, visible: !!s.visible,
      opacity: null, count: featureCount(s.geojson), geojson: s.geojson || null, sources: [s.id],
      removable: true, zoomable: !!s.geojson, exportable: !!s.geojson,
      swatch: { type: 'solid', color: '#00b8de', shape: s.type === 'raster' ? 'fill' : dominantGeometry(s.geojson) },
    });
  });
  return entries;
}

// ── draw order ──────────────────────────────────────────────────────────────

/**
 * Stored order (bottom to top, may hold stale keys) -> order over exactly `keys`.
 * Unknown keys drop out; keys the order has not seen yet slot in by default rank.
 */
export function normalizeOrder(stored, keys) {
  const present = new Set(keys);
  const base = [];
  const seen = new Set();
  (Array.isArray(stored) ? stored : []).forEach((k) => { if (present.has(k) && !seen.has(k)) { seen.add(k); base.push(k); } });
  const missing = keys.filter((k) => !seen.has(k))
    .map((k, i) => ({ k, i })).sort((a, b) => defaultRank(a.k) - defaultRank(b.k) || a.i - b.i).map((x) => x.k);
  missing.forEach((k) => {
    const r = defaultRank(k);
    let at = 0;
    base.forEach((b, i) => { if (defaultRank(b) <= r) at = i + 1; });
    base.splice(at, 0, k);
  });
  return base;
}

/** Move one key: 'top' | 'bottom' | 'up' | 'down' (one step in the stack). */
export function moveInOrder(order, key, op) {
  const i = order.indexOf(key);
  if (i < 0) return order;
  const next = order.filter((k) => k !== key);
  if (op === 'top') next.push(key);
  else if (op === 'bottom') next.unshift(key);
  else if (op === 'up') next.splice(Math.min(i + 1, next.length), 0, key);
  else if (op === 'down') next.splice(Math.max(i - 1, 0), 0, key);
  else return order;
  return next;
}

/**
 * Drop `key` next to `targetKey`. Positions are VISUAL (the tree lists the top first):
 * 'above' puts it just above the target in the stack. Returns the same array when nothing changes.
 */
export function moveRelative(order, key, targetKey, position) {
  if (key === targetKey || !order.includes(key) || !order.includes(targetKey)) return order;
  const next = order.filter((k) => k !== key);
  const t = next.indexOf(targetKey);
  next.splice(position === 'above' ? t + 1 : t, 0, key);
  return next.every((k, i) => k === order[i]) ? order : next;
}

/** Keys to persist: only entries whose ids survive a reload. */
export function persistableOrder(order) { return order.filter(isStableKey); }

// ── tree ────────────────────────────────────────────────────────────────────

/**
 * A group's rows as display items: layers sharing a `sourceKey` (two or more) collapse into one
 * `{ type: 'sub', id, name, kind, entries }` at the position of their topmost member; the rest
 * stay `{ type: 'layer', entry }`.
 */
export function groupItems(rows) {
  const bySource = new Map();
  rows.forEach((e) => { if (e.sourceKey) bySource.set(e.sourceKey, [...(bySource.get(e.sourceKey) || []), e]); });
  const items = [];
  const done = new Set();
  rows.forEach((e) => {
    const members = e.sourceKey ? bySource.get(e.sourceKey) : null;
    if (!members || members.length < 2) { items.push({ type: 'layer', entry: e }); return; }
    if (done.has(e.sourceKey)) return;
    done.add(e.sourceKey);
    items.push({ type: 'sub', id: e.sourceKey, name: e.sourceName || e.name, kind: e.sourceKind, entries: members });
  });
  return items;
}

/** Collapsed-state key of a sub-group row. */
export const subCollapseKey = (sourceKey) => `sub:${sourceKey}`;

/**
 * Rows for the panel. `order` is bottom to top; rows come out top first.
 * Options: query (substring, case-insensitive), onlyVisible, campaign ('all' or a name),
 * mode ('grouped' | 'flat').
 */
export function buildTree(entries, order, { query = '', onlyVisible = false, campaign = 'all', mode = 'grouped' } = {}) {
  const pos = new Map(order.map((k, i) => [k, i]));
  const q = query.trim().toLowerCase();
  const shown = entries.filter((e) => !e.hidden)
    .filter((e) => (campaign === 'all' || e.kind !== 'project' || e.campaign === campaign))
    .sort((a, b) => (pos.get(b.key) ?? -1) - (pos.get(a.key) ?? -1));
  const pass = (e) => (!q || e.name.toLowerCase().includes(q)) && (!onlyVisible || e.visible);
  const matched = shown.filter(pass);
  const groups = GROUPS.map((g) => {
    const rows = matched.filter((e) => e.group === g.id);
    return {
      id: g.id, label: g.label,
      total: shown.filter((e) => e.group === g.id).length,
      rows, items: groupItems(rows),
    };
  }).filter((g) => (q || onlyVisible ? g.rows.length > 0 : g.total > 0));
  return { mode, groups, flat: matched, total: shown.length, shown: matched.length };
}

/** The focusable rows in display order: group headers (unless flat) and the layer rows they expose. */
export function visibleRows(tree, collapsed = {}) {
  if (tree.mode === 'flat') return tree.flat.map((e) => ({ type: 'layer', key: e.key }));
  const rows = [];
  tree.groups.forEach((g) => {
    rows.push({ type: 'group', id: g.id });
    if (collapsed[g.id]) return;
    g.items.forEach((it) => {
      if (it.type === 'layer') { rows.push({ type: 'layer', key: it.entry.key }); return; }
      rows.push({ type: 'sub', id: subCollapseKey(it.id) });
      if (!collapsed[subCollapseKey(it.id)]) it.entries.forEach((e) => rows.push({ type: 'layer', key: e.key }));
    });
  });
  return rows;
}

/**
 * Keyboard intent for a key on the focused row. Pure so the panel and the tests share it.
 * Returns { type: 'focus', index } | { type: 'toggleGroup', id, collapsed } |
 * { type: 'toggleVisible', key } | { type: 'remove', key } | { type: 'rename', key } | null.
 */
export function keyIntent(key, rows, index, { collapsed = {}, entryOf = () => null } = {}) {
  if (!rows.length) return null;
  const row = rows[index];
  const last = rows.length - 1;
  switch (key) {
    case 'ArrowDown': return { type: 'focus', index: Math.min(index + 1, last) };
    case 'ArrowUp': return { type: 'focus', index: Math.max(index - 1, 0) };
    case 'Home': return { type: 'focus', index: 0 };
    case 'End': return { type: 'focus', index: last };
    case 'ArrowLeft':
      return row?.type !== 'layer' && row && !collapsed[row.id] ? { type: 'toggleGroup', id: row.id, collapsed: true } : null;
    case 'ArrowRight':
      return row?.type !== 'layer' && row && collapsed[row.id] ? { type: 'toggleGroup', id: row.id, collapsed: false } : null;
    case ' ':
    case 'Spacebar':
      if (row && row.type !== 'layer') return { type: 'toggleGroup', id: row.id, collapsed: !collapsed[row.id] };
      return row ? { type: 'toggleVisible', key: row.key } : null;
    case 'Delete':
      if (row?.type !== 'layer') return null;
      return entryOf(row.key)?.removable ? { type: 'remove', key: row.key } : null;
    case 'F2':
      if (row?.type !== 'layer') return null;
      return entryOf(row.key)?.renamable ? { type: 'rename', key: row.key } : null;
    default: return null;
  }
}

// ── MapLibre draw order ─────────────────────────────────────────────────────

/**
 * Target MapLibre layer-id order. `current` is the style's layers bottom to top
 * ([{ id, source }]); `entriesInOrder` the entries bottom to top. Layers owned by an entry
 * (by source id) form one contiguous block, sorted by entry order, placed where the lowest
 * owned layer already is; every other layer (basemap, drawing, overlays) keeps its place.
 */
export function targetLayerOrder(current, entriesInOrder) {
  const ids = current.map((l) => l.id);
  const rankOfSource = new Map();
  entriesInOrder.forEach((e, i) => (e.sources || []).forEach((s) => rankOfSource.set(s, i)));
  const firstIdx = current.findIndex((l) => rankOfSource.has(l.source));
  if (firstIdx < 0) return ids;
  const owned = current.filter((l) => rankOfSource.has(l.source));
  const rest = current.filter((l) => !rankOfSource.has(l.source)).map((l) => l.id);
  const block = owned
    .map((l, i) => ({ id: l.id, r: rankOfSource.get(l.source), i }))
    .sort((a, b) => a.r - b.r || a.i - b.i).map((x) => x.id);
  // Everything before the first owned layer is non-owned, so firstIdx is the split point in `rest`.
  return [...rest.slice(0, firstIdx), ...block, ...rest.slice(firstIdx)];
}

/** The `moveLayer(id, beforeId)` calls that turn `currentIds` into `target`, working top down. */
export function planMoves(currentIds, target) {
  const order = [...currentIds];
  const moves = [];
  for (let i = target.length - 1; i >= 0; i--) {
    if (order[i] === target[i]) continue;
    const id = target[i];
    const before = target[i + 1];
    moves.push([id, before]);
    order.splice(order.indexOf(id), 1);
    order.splice(before === undefined ? order.length : order.indexOf(before), 0, id);
  }
  return moves;
}

// ── layer opacity ───────────────────────────────────────────────────────────

/** Multiply the numeric `*-opacity` paint values by factor (0..1); fills in the default when absent. */
export function scalePaintOpacity(paint, kind, factor) {
  if (factor === undefined || factor === null || factor >= 1) return paint;
  const out = { ...paint };
  const keys = { circle: ['circle-opacity', 'circle-stroke-opacity'], line: ['line-opacity'], fill: ['fill-opacity'] }[kind] || [];
  keys.forEach((k) => {
    if (typeof out[k] === 'number') out[k] = out[k] * factor;
    else if (out[k] === undefined) out[k] = factor;
  });
  return out;
}
